import React, { useMemo, useCallback } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { formatTimestampInTimezone, isTimestampThreeHourly } from 'utils/time';
import type { WindTimestep } from 'types/wind';
import WindHourReading from 'components/WindHourReading';
import { isNumber } from 'lodash';
import styles from './WindForecastData.module.scss';
import type { LiveWindPoint } from '../../types/liveWind';

interface Props {
  barSpacing?: number;
  barWidth?: number;
  graphWidth: number;
  isLive?: boolean;
  isOnshoreOffshoreGraph?: boolean;
  wind: Array<LiveWindPoint> | Array<WindTimestep>;
}

const WindForecastData: React.FC<Props> = ({
  barSpacing = null,
  barWidth = 2,
  graphWidth,
  isLive = true,
  isOnshoreOffshoreGraph = true,
  wind,
}) => {
  // default barSpacing calculation to live wind modal where there are 144 data
  // points, each with a 2px bar width
  const HOUR_READING_WIDTH = useMemo(
    () =>
      barWidth +
      (barSpacing || (graphWidth - (wind?.length || 0) * barWidth) / (wind?.length || 0)),
    [graphWidth, barSpacing, barWidth, wind?.length],
  );

  const labelWidth: number = useMemo(() => {
    if (isOnshoreOffshoreGraph) {
      return 6 * HOUR_READING_WIDTH;
    }
    if (isLive) {
      return HOUR_READING_WIDTH - barWidth / 2;
    }
    return barWidth;
  }, [HOUR_READING_WIDTH, barWidth, isLive, isOnshoreOffshoreGraph]);

  const showLabelGroup = (index: number) => (isLive && index % 6 === 0) || !isLive;

  const displayCurrentCondition = (
    index: number,
    timestamp: number,
    utcOffset: number,
  ): boolean => {
    if (!isNumber(timestamp) || !isNumber(utcOffset)) {
      return false;
    }
    if (isLive) {
      return index % 18 === 0;
    }
    return isTimestampThreeHourly(timestamp, utcOffset);
  };

  const windReading = useCallback(
    (reading: LiveWindPoint | WindTimestep) => ({
      timestamp: reading.timestamp,
      speed: (reading as LiveWindPoint)?.wind?.speed || (reading as WindTimestep)?.speed || 0,
      gust: (reading as LiveWindPoint)?.wind?.gust || (reading as WindTimestep)?.gust || 0,
      direction:
        (reading as LiveWindPoint)?.wind?.direction || (reading as WindTimestep)?.direction || 0,
    }),
    [],
  );

  const leftMargin = useMemo(
    () => (barSpacing || graphWidth - (wind?.length || 0) * barWidth) / 2,
    [graphWidth, barSpacing, barWidth, wind?.length],
  );

  return (
    <Stack
      direction="column"
      maxWidth={`${graphWidth}px`}
      width={isOnshoreOffshoreGraph ? '100%' : 'auto'}
      data-testid="live-wind-forecast-data"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        textAlign="left"
        width="100%"
        marginLeft={isOnshoreOffshoreGraph ? 0 : `${leftMargin}px`}
        className={styles.windReadings}
      >
        {wind?.map(
          (reading, i) =>
            showLabelGroup(i) && (
              <Box key={reading?.timestamp} width="100%" textAlign="center">
                {displayCurrentCondition(i, reading.timestamp, reading.utcOffset) && (
                  <WindHourReading
                    key={reading.timestamp}
                    reading={windReading(reading)}
                    hourReadingWidth={labelWidth}
                  />
                )}
              </Box>
            ),
        )}
      </Stack>

      {wind && (
        <Box width="100%" sx={{ backgroundColor: 'white' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            paddingTop={isOnshoreOffshoreGraph ? '5px' : 0}
            width="100%"
            sx={{ backgroundColor: 'white' }}
            marginLeft={isOnshoreOffshoreGraph ? 0 : `${leftMargin}px`}
          >
            {wind.map(
              (reading, i) =>
                showLabelGroup(i) && (
                  <Box key={reading.timestamp} textAlign="center" width="100%">
                    <Box
                      maxWidth={labelWidth}
                      className={!isOnshoreOffshoreGraph ? styles.containerWithTickMark : ''}
                    >
                      {displayCurrentCondition(i, reading.timestamp, reading.utcOffset) && (
                        <Typography
                          key={reading.timestamp}
                          variant="caption2"
                          color="text.secondary"
                          align="center"
                          data-testid="live-wind-hour-label"
                        >
                          {formatTimestampInTimezone(reading.timestamp, reading.utcOffset, 'h')
                            .toLowerCase()
                            .replace('12pm', 'Noon')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ),
            )}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default React.memo(WindForecastData);
